/**
 * Shared overlay positioning constants for consistent placement of
 * buttons, badges, and controls on top of images/media.
 *
 * Standard inset: 16px (Tailwind's `4`) from edges
 * Used by: StashMediaCarousel, EntityHeader, StashDetailPage
 */

/** Standard inset from edges in Tailwind units */
export const OVERLAY_INSET = "4";

/** Corner positions - use these for buttons/badges in corners */
export const OVERLAY_POSITION = {
  topLeft: "absolute top-4 left-4 z-10",
  topRight: "absolute top-4 right-4 z-10",
  bottomLeft: "absolute bottom-4 left-4 z-10",
  bottomRight: "absolute bottom-4 right-4 z-10",
  bottomCenter: "absolute bottom-4 left-1/2 -translate-x-1/2 z-10",
  centerLeft: "absolute left-4 top-1/2 -translate-y-1/2 z-10",
  centerRight: "absolute right-4 top-1/2 -translate-y-1/2 z-10",
} as const;

/** Inline styles for safe-area-aware positioning (shared by both absolute and fixed variants) */
const SAFE_STYLE = {
  topLeft: { top: "calc(var(--sat) + 16px)", left: "calc(var(--sal) + 16px)" },
  topRight: { top: "calc(var(--sat) + 16px)", right: "calc(var(--sar) + 16px)" },
} as const;

/**
 * Safe area aware positioning for iOS (absolute).
 * Respects the notch/dynamic island with 16px inset matching top-4/left-4/right-4.
 */
export const OVERLAY_POSITION_SAFE = {
  topLeft: { className: "absolute z-10", style: SAFE_STYLE.topLeft },
  topRight: { className: "absolute z-10", style: SAFE_STYLE.topRight },
} as const;

/**
 * Non-sticky safe-area positioning for page actions rendered over media.
 * z-20 keeps these controls above carousel and video overlays at z-10.
 */
export const OVERLAY_POSITION_SAFE_ABOVE_MEDIA = {
  topLeft: { className: "absolute z-20", style: SAFE_STYLE.topLeft },
  topRight: { className: "absolute z-20", style: SAFE_STYLE.topRight },
} as const;

/**
 * Fixed positioning variant for mobile detail pages.
 * Buttons stay fixed to the viewport while scrolling on mobile,
 * but revert to absolute positioning on desktop.
 * z-20 ensures they render above scrolling z-10 content (carousels, gradients).
 */
export const OVERLAY_POSITION_SAFE_FIXED = {
  topLeft: { className: "fixed desktop:absolute z-20", style: SAFE_STYLE.topLeft },
  topRight: { className: "fixed desktop:absolute z-20", style: SAFE_STYLE.topRight },
} as const;
